import type { Approval, PersonTaskPayload } from '@deskworks/shared-types';
import { PERSON_TASK_APPROVAL } from '@deskworks/shared-types';
import { Link } from '@tanstack/react-router';
import { Check, Hand, RefreshCw, X } from 'lucide-react';
import { useState } from 'react';
import { Markdown } from '@/components/markdown';
import { RelativeTime } from '@/components/relative-time';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

/** Something only a person can do, asked by the Lead (docs/features/agent-mode-and-approvals.md#asking-a-person-to-do-something). */
export const isPersonTask = (a: Pick<Approval, 'type'>) => a.type === PERSON_TASK_APPROVAL;

/**
 * What the Lead asks a person to do — top up a balance, sign in, give access: what to do, how the Lead can tell it is
 * done, and "Done, go on" (or what the Lead named the button, e.g. "Balance is topped up") / "Can't do it". Nobody
 * decides it in the person's place, not even in God mode.
 */
export function PersonTaskCard({
  approval,
  workspaceId,
  desk,
  onDecided,
}: {
  approval: Approval;
  workspaceId: string;
  /** Shown above the ask where it isn't clear which desk asks (the inbox). */
  desk?: { id: string; name: string };
  onDecided?: () => void;
}) {
  const [comment, setComment] = useState('');
  const payload = approval.payload as unknown as PersonTaskPayload;
  const invalidate = [qk.workItem(approval.workItemId), qk.approvalsAll(workspaceId), qk.overview(workspaceId), qk.desks(workspaceId)];
  const body = () => ({ comment: comment.trim() || undefined });
  const done = useApiMutation(() => api.approvals.approve(approval.id, body()), {
    invalidate,
    success: t('Thanks — the Lead goes on.'),
    onSuccess: onDecided,
  });
  const cant = useApiMutation(() => api.approvals.reject(approval.id, body()), {
    invalidate,
    success: t('The Lead looks for another way.'),
    onSuccess: onDecided,
  });
  const busy = done.isPending || cant.isPending;

  return (
    <Card className="border-amber-300 bg-amber-50/60 p-4 dark:border-amber-800/70 dark:bg-amber-950/20">
      <div className="flex items-start gap-3">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400">
          <Hand className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              {desk && (
                <Link
                  to="/w/$workspaceId/desks/$deskId"
                  params={{ workspaceId, deskId: desk.id }}
                  className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                >
                  {desk.name}
                </Link>
              )}
              <p className="text-xs font-medium text-amber-700 dark:text-amber-400">{t('Only a person can do this')}</p>
              <h3 className="text-sm font-semibold break-words">{approval.description}</h3>
            </div>
            <RelativeTime value={approval.requestedAt} className="shrink-0 text-xs text-muted-foreground" />
          </div>
          {payload?.steps && <Markdown text={payload.steps} className="mt-2" />}
          {payload?.doneWhen && (
            <p className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground">
              <RefreshCw className="mt-0.5 size-3 shrink-0" />
              <span>{t('Closes by itself once the Lead sees: {check}', { check: payload.doneWhen })}</span>
            </p>
          )}
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('Note for the Lead (optional) — e.g. what you did or why it can’t be done')}
            rows={1}
            className="mt-3 min-h-9 bg-card"
          />
          <div className="mt-2 flex flex-wrap gap-2">
            <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-600/90" loading={done.isPending} disabled={busy} onClick={() => done.mutate()}>
              {!done.isPending && <Check />} {payload?.doneLabel || t('Done, go on')}
            </Button>
            <Button size="sm" variant="outline" loading={cant.isPending} disabled={busy} onClick={() => cant.mutate()}>
              {!cant.isPending && <X />} {t('Can’t do it')}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
